import { Injectable } from '@angular/core';
import { StorageService } from '../../utils/storage/storage.service';
import { Insomnia } from '@ionic-native/insomnia/ngx';
import { Platform } from '@ionic/angular';
import { AudioManagement } from '@ionic-native/audio-management/ngx';
import { WatcherService } from '../../utils/watcher/watcher.service';
import { APP_SETTINGS_CHANGED } from '../../utils/watcher/watcher-event';

import size from 'lodash/size';
import has from 'lodash/has';
import forOwn from 'lodash/forOwn';
import cloneDeep from 'lodash/cloneDeep';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private _pluginKeys: string[] = ['keep_awake', 'force_max_volume'];

  private _bump_screen_config: any = {
    global: {},

    general: {
      keep_awake: true,
      force_max_volume: true,
      show_yesterday_orders: false,
      dark_mode: false,
    },
  };

  constructor(
    public storage: StorageService,
    public platform: Platform,
    public insomnia: Insomnia,
    public audioManagement: AudioManagement,
    public watcher: WatcherService,
  ) {}

  async loadGlobalSettings(): Promise<any> {
    const globalSettings = await this.storage.load('Global_settings');
    if (globalSettings) {
      forOwn(globalSettings, (value, key) => {
        if (has(this._bump_screen_config['global'], key)) {
          this._bump_screen_config['global'][key] = value;
        }
      });
    }

    return;
  }

  /**
   * Load the app settings of the bump screen
   * @param id - The bump screen id
   */
  async loadSettings(id: string): Promise<any> {
    const userSettings = await this.storage.load('Settings_' + id);
    if (size(userSettings)) {
      // split by module
      forOwn(userSettings, (val, module) => {
        if (module == 'global') return;

        if (has(this._bump_screen_config, module)) {
          // assign by key
          forOwn(val, (value, key) => {
            if (has(this._bump_screen_config[module], key)) {
              this._bump_screen_config[module][key] = value;
            }
          });
        }
      });
    } else {
      // don't do anything st00pid
      // maybe keep the default for now
    }

    this.pluginSettings('all');

    // broadcast a change and loaded
    this.watcher.broadcast(APP_SETTINGS_CHANGED, this._bump_screen_config);
    return;
  }

  /**
   * Saves the setting of the bump screen
   * @param id - The bump screen id
   */
  async saveSettings(id: string): Promise<any> {
    this.watcher.broadcast(APP_SETTINGS_CHANGED, this._bump_screen_config);

    // clone settings
    let cloneSettings = cloneDeep(this._bump_screen_config);
    await this.storage.save('Global_settings', cloneSettings.global);

    delete cloneSettings.global;
    return this.storage.save('Settings_' + id, cloneSettings);
  }

  /**
   * Retrieves a specific setting value
   * @param module - The module to which the setting belongs
   * @param key - The key of the setting
   */
  public getBumpScreenSettingByKey(module: string, key: string): any {
    if (size(this._bump_screen_config[module]) && has(this._bump_screen_config[module], key)) {
      return this._bump_screen_config[module][key];
    } else {
      return null;
    }
  }

  /**
   * Sets the value of a setting
   * @param module - The module to which the setting belongs
   * @param key - The key of the setting
   */
  public setBumpScreenSettingByKey(module: string, key: string, value: any): void {
    if (size(this._bump_screen_config[module]) && has(this._bump_screen_config[module], key)) {
      if (module == 'general' && this._pluginKeys.indexOf(key) > -1) {
        this.pluginSettings(key);
      }

      this._bump_screen_config[module][key] = value;
    }
  }

  /**
   * Returns the raw value of all the available setting
   */
  public getAllConfig(): any {
    return this._bump_screen_config;
  }

  /**
   * Triggers plugin changes
   * @param key - The plugin key to update
   */
  async pluginSettings(key: string) {
    if (this.platform.is('cordova')) {
      // KEEP AWAKE
      if (key == 'keep_awake' || key == 'all') {
        const keepAwake: boolean = this.getBumpScreenSettingByKey('general', 'keep_awake');
        if (keepAwake) {
          await this.insomnia.keepAwake();
        } else {
          await this.insomnia.allowSleepAgain();
        }
      }

      // VOLUME
      if (key == 'force_max_volume' || key == 'all') {
        const useMaxVolume: boolean = this.getBumpScreenSettingByKey('general', 'force_max_volume');
        if (useMaxVolume) {
          const { maxVolume } = await this.audioManagement.getMaxVolume(AudioManagement.VolumeType.MUSIC);
          const { volume } = await this.audioManagement.getVolume(AudioManagement.VolumeType.MUSIC);

          if (volume != maxVolume) {
            this.audioManagement.setVolume(AudioManagement.VolumeType.MUSIC, maxVolume);
          }
        }
      }
    }
  }
}
